import {useState} from "react";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";

import {castToValidationError} from "@pg-mono/react-hook-forms-utils";
import {isFetchBaseQueryError} from "@pg-mono/react-hook-forms-utils/src/utils/is_fetch_base_query_error";

import {toJsonValues} from "../../../form/translate/to_json_values";
import {addSuccessNotification} from "../../../notifications/utils/add_success_notification";
import {addAppliedOffers} from "../../../offer/map/actions/add_applied_offers";
import {IRecommendedOffer} from "../../../offer/types/IRecommendedOffer";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {useAppDispatch} from "../../../store/hooks";
import {Currency} from "../../../types/Currency";
import {useCreateMultipleApplicationsMutation} from "../../api/create_multiple_applications";
import {applicationFormFields} from "../../constants/form/application_form_fields";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IPostMultipleApplicationsPayload} from "../../types/lead/IPostMultipleApplicaitonsPayload";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadRecommendedPropertyType} from "../../types/multi_lead/ApplicationMultiLeadRecommendedPropertyType";
import {addPropertiesToStorageSentApplication} from "../../utils/application_property_storage";
import {alertFormSuccessMessage} from "../../utils/form/application_form_alert_text";
import {ApplicationSource, ApplicationSourceSection} from "../../utils/form/application_form_source";
import {getGeneratedMessage} from "../../utils/form/get_generated_message";
import {addOffersToStorageSentApplication} from "../../utils/multi_lead/get_storage_sent_applications_id_list_by";
import {useMultiLeadData} from "./use_application_multi_lead_data";
import {useMultiLeadRecommendations} from "./use_application_multi_lead_recommendations";
import {useMultiLeadReport} from "./use_multi_lead_report";
import {RecommendedOfferApplicationsType} from "./use_track_recommended_offers_application";

interface IProps {
    recommendationType: ApplicationMultiLeadRecommendationType;
    sourceSection: ApplicationSourceSection;
    originalApplicationFormValues: IApplicationFormValues | null;
    recommendationsWithPriceFilter: boolean;
    customSourceSection?: string;
    currency?: Currency;
    castRtkError?: (error: FetchBaseQueryError) => void;
}

type ISendMultiLeadFormParams =
    | {
          selectedRecommendations: number[];
          recommendedProperties?: never;
          recommendedOffers?: never;
          formValues: IApplicationFormValues;
      }
    | {
          selectedRecommendations?: never;
          recommendedProperties?: IRecommendedProperty[];
          recommendedOffers?: IRecommendedOffer[];
          formValues: IApplicationFormValues;
      };

function isPropertyDetail(property: IRecommendedProperty | IPropertyDetail): property is IPropertyDetail {
    return "region" in property.offer;
}

export const useMultiLeadFormSend = (props: IProps) => {
    const dispatch = useAppDispatch();
    const [createMultipleApps] = useCreateMultipleApplicationsMutation();
    const [isMultiLeadSending, setMultiLeadSending] = useState(false);
    const [sentPropertiesApplications, setSentPropertiesApplications] = useState<ApplicationMultiLeadRecommendedPropertyType[]>([]);
    const {applicationData, viewType, isAuthenticated} = useMultiLeadData();
    const {recommendedOffers, recommendedProperties} = useMultiLeadRecommendations({
        propertyId: applicationData?.property?.detail?.id,
        offerId: applicationData?.offer?.detail?.id
    });
    const {singleApplicationSuccess, finishedSending} = useMultiLeadReport();
    const refererApplicationUUID = applicationData?.application?.uuid;

    const sendPropertyApplications = async (
        recommendedPropertiesToSend: (IRecommendedProperty | IPropertyDetail)[],
        formValues: IApplicationFormValues
    ): Promise<ApplicationMultiLeadRecommendedPropertyType[]> => {
        // TODO: this shouldn't be JsonValues type, refactor when migrating to rhf
        const jsonValues = toJsonValues(applicationFormFields, formValues) as Pick<IApplicationFormValues, "name" | "marketing_email" | "marketing_sms">;
        const source = ApplicationSource.RecommendedProperty;

        if ("text" in jsonValues) {
            delete jsonValues.text;
        }

        const createPayload: IPostMultipleApplicationsPayload = {
            ...jsonValues,
            home_match_trigger: false,
            referer_application_uuid: refererApplicationUUID,
            source,
            source_section: props.sourceSection,
            applications: recommendedPropertiesToSend.map((property) => ({
                property: property.id,
                text: getGeneratedMessage({
                    offerType: property.offer.type,
                    offerName: property.offer.name,
                    propertyNumber: property.number,
                    name: formValues.name,
                    rooms: property.rooms,
                    area: property.area
                })
            }))
        };

        const response = await createMultipleApps(createPayload).unwrap();

        dispatch(addSuccessNotification(alertFormSuccessMessage));

        const applications = response.applications;

        if (!applications.filter(Boolean).length) {
            return [];
        }

        const offerIDs = applications.reduce<number[]>((acc, application) => {
            return application ? acc.concat(application.offer) : acc;
        }, []);

        addOffersToStorageSentApplication(
            recommendedPropertiesToSend.map((property) => {
                const offerRegion = isPropertyDetail(property) ? property.offer.region : property.region;

                return {
                    id: property.offer.id,
                    coordinates: property.offer.geo_point.coordinates,
                    country: offerRegion.country,
                    regionId: offerRegion.id
                };
            })
        );

        addPropertiesToStorageSentApplication(recommendedPropertiesToSend.map((property) => property.id));

        if (isAuthenticated) {
            dispatch(addAppliedOffers(offerIDs));
        }

        const createdApplications = applications.reduce<ApplicationMultiLeadRecommendedPropertyType[]>((acc, application) => {
            if (application) {
                const recommendedProperty = recommendedPropertiesToSend.find(({id}) => id === application.property);

                singleApplicationSuccess(application, props.sourceSection, viewType);

                if (recommendedProperty) {
                    acc.push([application, recommendedProperty]);
                }
            }
            return acc;
        }, []);

        finishedSending(
            ApplicationMultiLeadRecommendationType.PROPERTY,
            createdApplications,
            viewType,
            "standard",
            applicationData?.application,
            props.recommendationsWithPriceFilter,
            props.customSourceSection,
            props.currency
        );

        return createdApplications;
    };

    const sendOfferApplications = async (
        selectedRecommendationsToSend: IRecommendedOffer[],
        formValues: IApplicationFormValues
    ): Promise<RecommendedOfferApplicationsType[]> => {
        // TODO: this shouldn't be JsonValues type, refactor when migrating to rhf
        const jsonValues = toJsonValues(applicationFormFields, formValues) as Pick<IApplicationFormValues, "name" | "marketing_email" | "marketing_sms">;
        const source = ApplicationSource.RecommendedOffer;

        if ("text" in jsonValues) {
            delete jsonValues.text;
        }

        const createPayload: IPostMultipleApplicationsPayload = {
            ...jsonValues,
            home_match_trigger: false,
            referer_application_uuid: refererApplicationUUID,
            source,
            source_section: props.sourceSection,
            applications: selectedRecommendationsToSend.map((recommendedOffer) => ({
                text: getGeneratedMessage({
                    offerType: recommendedOffer.type,
                    offerName: recommendedOffer.name,
                    name: formValues.name
                }),
                offer: recommendedOffer.id
            }))
        };

        const response = await createMultipleApps(createPayload).unwrap();

        dispatch(addSuccessNotification(alertFormSuccessMessage));

        const applications = response.applications;

        if (!applications.filter(Boolean).length) {
            return [];
        }

        const offerIDs = applications.reduce<number[]>((acc, application) => {
            return application ? acc.concat(application.offer) : acc;
        }, []);

        if (isAuthenticated) {
            dispatch(addAppliedOffers(offerIDs));
        }

        addOffersToStorageSentApplication(
            selectedRecommendationsToSend.map((offer) => {
                return {id: offer.id, coordinates: offer.geo_point.coordinates, country: offer.region.country, regionId: offer.region.parent?.id};
            })
        );

        const createdApplications = applications.reduce<RecommendedOfferApplicationsType[]>((acc, application) => {
            if (application) {
                const recommendedOffer = selectedRecommendationsToSend.find(({id}) => id === application.offer);

                singleApplicationSuccess(application, props.sourceSection, viewType);

                if (recommendedOffer) {
                    acc.push([application, recommendedOffer]);
                }
            }
            return acc;
        }, []);

        finishedSending(
            ApplicationMultiLeadRecommendationType.OFFER,
            createdApplications,
            viewType,
            "standard",
            applicationData?.application,
            false,
            undefined,
            props.currency
        );

        return createdApplications;
    };

    const sendMultiLeadForm = async (
        sendMultiLeadFormParams: ISendMultiLeadFormParams
    ): Promise<ApplicationMultiLeadRecommendedPropertyType[] | RecommendedOfferApplicationsType[]> => {
        setMultiLeadSending(true);

        if (props.recommendationType === ApplicationMultiLeadRecommendationType.PROPERTY) {
            const propertiesToSend =
                "recommendedProperties" in sendMultiLeadFormParams && sendMultiLeadFormParams.recommendedProperties
                    ? sendMultiLeadFormParams.recommendedProperties || []
                    : recommendedProperties.filter((property) => sendMultiLeadFormParams.selectedRecommendations?.includes(property.id));

            return sendPropertyApplications(propertiesToSend, sendMultiLeadFormParams.formValues)
                .then((response) => {
                    setSentPropertiesApplications((prev) => [...prev, ...response]);

                    return response;
                })
                .catch((error) => {
                    if (isFetchBaseQueryError(error) && error.status === 400) {
                        throw props.castRtkError ? props.castRtkError(error) : castToValidationError(error);
                    }
                    return [];
                })
                .finally(() => {
                    setMultiLeadSending(false);
                });
        }

        if (props.recommendationType === ApplicationMultiLeadRecommendationType.OFFER) {
            const offersToSend =
                "recommendedOffers" in sendMultiLeadFormParams && sendMultiLeadFormParams.recommendedOffers
                    ? sendMultiLeadFormParams.recommendedOffers || []
                    : recommendedOffers.filter((offer) => sendMultiLeadFormParams.selectedRecommendations?.includes(offer.id));

            return sendOfferApplications(offersToSend, sendMultiLeadFormParams.formValues)
                .then((response) => {
                    return response;
                })
                .catch(() => {
                    return [];
                })
                .finally(() => {
                    setMultiLeadSending(false);
                });
        }

        setMultiLeadSending(false);
        throw new Error("Unknown recommendation type");
    };

    return {
        sendMultiLeadForm,
        isMultiLeadSending,
        sentPropertiesApplications
    };
};
import {useState} from "react";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";

import {castToValidationError} from "@pg-mono/react-hook-forms-utils";
import {isFetchBaseQueryError} from "@pg-mono/react-hook-forms-utils/src/utils/is_fetch_base_query_error";

import {toJsonValues} from "../../../form/translate/to_json_values";
import {addSuccessNotification} from "../../../notifications/utils/add_success_notification";
import {addAppliedOffers} from "../../../offer/map/actions/add_applied_offers";
import {IRecommendedOffer} from "../../../offer/types/IRecommendedOffer";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {useAppDispatch} from "../../../store/hooks";
import {Currency} from "../../../types/Currency";
import {useCreateMultipleApplicationsMutation} from "../../api/create_multiple_applications";
import {applicationFormFields} from "../../constants/form/application_form_fields";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IPostMultipleApplicationsPayload} from "../../types/lead/IPostMultipleApplicaitonsPayload";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadRecommendedPropertyType} from "../../types/multi_lead/ApplicationMultiLeadRecommendedPropertyType";
import {addPropertiesToStorageSentApplication} from "../../utils/application_property_storage";
import {alertFormSuccessMessage} from "../../utils/form/application_form_alert_text";
import {ApplicationSource, ApplicationSourceSection} from "../../utils/form/application_form_source";
import {getGeneratedMessage} from "../../utils/form/get_generated_message";
import {addOffersToStorageSentApplication} from "../../utils/multi_lead/get_storage_sent_applications_id_list_by";
import {useMultiLeadData} from "./use_application_multi_lead_data";
import {useMultiLeadRecommendations} from "./use_application_multi_lead_recommendations";
import {useMultiLeadReport} from "./use_multi_lead_report";
import {RecommendedOfferApplicationsType} from "./use_track_recommended_offers_application";

interface IProps {
    recommendationType: ApplicationMultiLeadRecommendationType;
    sourceSection: ApplicationSourceSection;
    originalApplicationFormValues: IApplicationFormValues | null;
    recommendationsWithPriceFilter: boolean;
    customSourceSection?: string;
    currency?: Currency;
    castRtkError?: (error: FetchBaseQueryError) => void;
}

type ISendMultiLeadFormParams =
    | {
          selectedRecommendations: number[];
          recommendedProperties?: never;
          recommendedOffers?: never;
          formValues: IApplicationFormValues;
      }
    | {
          selectedRecommendations?: never;
          recommendedProperties?: IRecommendedProperty[];
          recommendedOffers?: IRecommendedOffer[];
          formValues: IApplicationFormValues;
      };

function isPropertyDetail(property: IRecommendedProperty | IPropertyDetail): property is IPropertyDetail {
    return "region" in property.offer;
}

export const useMultiLeadFormSend = (props: IProps) => {
    const dispatch = useAppDispatch();
    const [createMultipleApps] = useCreateMultipleApplicationsMutation();
    const [isMultiLeadSending, setMultiLeadSending] = useState(false);
    const [sentPropertiesApplications, setSentPropertiesApplications] = useState<ApplicationMultiLeadRecommendedPropertyType[]>([]);
    const {applicationData, viewType, isAuthenticated} = useMultiLeadData();
    const {recommendedOffers, recommendedProperties} = useMultiLeadRecommendations({
        propertyId: applicationData?.property?.detail?.id,
        offerId: applicationData?.offer?.detail?.id
    });
    const {singleApplicationSuccess, finishedSending} = useMultiLeadReport();
    const refererApplicationUUID = applicationData?.application?.uuid;

    const sendPropertyApplications = async (
        recommendedPropertiesToSend: (IRecommendedProperty | IPropertyDetail)[],
        formValues: IApplicationFormValues
    ): Promise<ApplicationMultiLeadRecommendedPropertyType[]> => {
        // TODO: this shouldn't be JsonValues type, refactor when migrating to rhf
        const jsonValues = toJsonValues(applicationFormFields, formValues) as Pick<IApplicationFormValues, "name" | "marketing_email" | "marketing_sms">;
        const source = ApplicationSource.RecommendedProperty;

        if ("text" in jsonValues) {
            delete jsonValues.text;
        }

        const createPayload: IPostMultipleApplicationsPayload = {
            ...jsonValues,
            home_match_trigger: false,
            referer_application_uuid: refererApplicationUUID,
            source,
            source_section: props.sourceSection,
            applications: recommendedPropertiesToSend.map((property) => ({
                property: property.id,
                text: getGeneratedMessage({
                    offerType: property.offer.type,
                    offerName: property.offer.name,
                    propertyNumber: property.number,
                    name: formValues.name,
                    rooms: property.rooms,
                    area: property.area
                })
            }))
        };

        const response = await createMultipleApps(createPayload).unwrap();

        dispatch(addSuccessNotification(alertFormSuccessMessage));

        const applications = response.applications;

        if (!applications.filter(Boolean).length) {
            return [];
        }

        const offerIDs = applications.reduce<number[]>((acc, application) => {
            return application ? acc.concat(application.offer) : acc;
        }, []);

        addOffersToStorageSentApplication(
            recommendedPropertiesToSend.map((property) => {
                const offerRegion = isPropertyDetail(property) ? property.offer.region : property.region;

                return {
                    id: property.offer.id,
                    coordinates: property.offer.geo_point.coordinates,
                    country: offerRegion.country,
                    regionId: offerRegion.id
                };
            })
        );

        addPropertiesToStorageSentApplication(recommendedPropertiesToSend.map((property) => property.id));

        if (isAuthenticated) {
            dispatch(addAppliedOffers(offerIDs));
        }

        const createdApplications = applications.reduce<ApplicationMultiLeadRecommendedPropertyType[]>((acc, application) => {
            if (application) {
                const recommendedProperty = recommendedPropertiesToSend.find(({id}) => id === application.property);

                singleApplicationSuccess(application, props.sourceSection, viewType);

                if (recommendedProperty) {
                    acc.push([application, recommendedProperty]);
                }
            }
            return acc;
        }, []);

        finishedSending(
            ApplicationMultiLeadRecommendationType.PROPERTY,
            createdApplications,
            viewType,
            "standard",
            applicationData?.application,
            props.recommendationsWithPriceFilter,
            props.customSourceSection,
            props.currency
        );

        return createdApplications;
    };

    const sendOfferApplications = async (
        selectedRecommendationsToSend: IRecommendedOffer[],
        formValues: IApplicationFormValues
    ): Promise<RecommendedOfferApplicationsType[]> => {
        // TODO: this shouldn't be JsonValues type, refactor when migrating to rhf
        const jsonValues = toJsonValues(applicationFormFields, formValues) as Pick<IApplicationFormValues, "name" | "marketing_email" | "marketing_sms">;
        const source = ApplicationSource.RecommendedOffer;

        if ("text" in jsonValues) {
            delete jsonValues.text;
        }

        const createPayload: IPostMultipleApplicationsPayload = {
            ...jsonValues,
            home_match_trigger: false,
            referer_application_uuid: refererApplicationUUID,
            source,
            source_section: props.sourceSection,
            applications: selectedRecommendationsToSend.map((recommendedOffer) => ({
                text: getGeneratedMessage({
                    offerType: recommendedOffer.type,
                    offerName: recommendedOffer.name,
                    name: formValues.name
                }),
                offer: recommendedOffer.id
            }))
        };

        const response = await createMultipleApps(createPayload).unwrap();

        dispatch(addSuccessNotification(alertFormSuccessMessage));

        const applications = response.applications;

        if (!applications.filter(Boolean).length) {
            return [];
        }

        const offerIDs = applications.reduce<number[]>((acc, application) => {
            return application ? acc.concat(application.offer) : acc;
        }, []);

        if (isAuthenticated) {
            dispatch(addAppliedOffers(offerIDs));
        }

        addOffersToStorageSentApplication(
            selectedRecommendationsToSend.map((offer) => {
                return {id: offer.id, coordinates: offer.geo_point.coordinates, country: offer.region.country, regionId: offer.region.parent?.id};
            })
        );

        const createdApplications = applications.reduce<RecommendedOfferApplicationsType[]>((acc, application) => {
            if (application) {
                const recommendedOffer = selectedRecommendationsToSend.find(({id}) => id === application.offer);

                singleApplicationSuccess(application, props.sourceSection, viewType);

                if (recommendedOffer) {
                    acc.push([application, recommendedOffer]);
                }
            }
            return acc;
        }, []);

        finishedSending(
            ApplicationMultiLeadRecommendationType.OFFER,
            createdApplications,
            viewType,
            "standard",
            applicationData?.application,
            false,
            undefined,
            props.currency
        );

        return createdApplications;
    };

    const sendMultiLeadForm = async (
        sendMultiLeadFormParams: ISendMultiLeadFormParams
    ): Promise<ApplicationMultiLeadRecommendedPropertyType[] | RecommendedOfferApplicationsType[]> => {
        setMultiLeadSending(true);

        if (props.recommendationType === ApplicationMultiLeadRecommendationType.PROPERTY) {
            const propertiesToSend =
                "recommendedProperties" in sendMultiLeadFormParams && sendMultiLeadFormParams.recommendedProperties
                    ? sendMultiLeadFormParams.recommendedProperties || []
                    : recommendedProperties.filter((property) => sendMultiLeadFormParams.selectedRecommendations?.includes(property.id));

            return sendPropertyApplications(propertiesToSend, sendMultiLeadFormParams.formValues)
                .then((response) => {
                    setSentPropertiesApplications((prev) => [...prev, ...response]);

                    return response;
                })
                .catch((error) => {
                    if (isFetchBaseQueryError(error) && error.status === 400) {
                        throw props.castRtkError ? props.castRtkError(error) : castToValidationError(error);
                    }
                    return [];
                })
                .finally(() => {
                    setMultiLeadSending(false);
                });
        }

        if (props.recommendationType === ApplicationMultiLeadRecommendationType.OFFER) {
            const offersToSend =
                "recommendedOffers" in sendMultiLeadFormParams && sendMultiLeadFormParams.recommendedOffers
                    ? sendMultiLeadFormParams.recommendedOffers || []
                    : recommendedOffers.filter((offer) => sendMultiLeadFormParams.selectedRecommendations?.includes(offer.id));

            return sendOfferApplications(offersToSend, sendMultiLeadFormParams.formValues)
                .then((response) => {
                    return response;
                })
                .catch(() => {
                    return [];
                })
                .finally(() => {
                    setMultiLeadSending(false);
                });
        }

        setMultiLeadSending(false);
        throw new Error("Unknown recommendation type");
    };

    return {
        sendMultiLeadForm,
        isMultiLeadSending,
        sentPropertiesApplications
    };
};
